import { collection, getDocs, query, where, orderBy, doc, updateDoc, addDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { memberMembershipFromFirestore } from "../models/memberMembership";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";
import { toISODate } from "../utils/date";


/**
 * MemberMembershipService
 * Centraliza as operações de banco de dados para os planos adquiridos pelos membros.
 * Coleção: tenants/{idTenant}/memberMemberships
 * 
 * MemberMembership = instância de um plano (membership) comprado por um membro.
 * Criado automaticamente pelo SalesService.processSale().
 */
const MemberMembershipService = {
    /**
     * Busca todos os planos adquiridos por um membro específico.
     * Ordenados por data de criação (mais recente primeiro).
     */
    getByMember: async (idTenant, idMember) => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do Membro são obrigatórios.");

        try {
            const colRef = collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS);
            const q = query(colRef, where("idMember", "==", idMember));
            const snapshot = await getDocs(q);

            return snapshot.docs.map(memberMembershipFromFirestore);
        } catch (error) {
            console.error("Erro ao buscar planos do membro:", error);
            throw error;
        }
    },

    /**
     * Busca todos os planos ativos de um membro.
     */
    getActiveByMember: async (idTenant, idMember) => {
        if (!idTenant || !idMember) throw new Error("ID do Tenant e do Membro são obrigatórios.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS),
                where("idMember", "==", idMember),
                where("status", "==", "active"),
                orderBy("startDate", "asc")
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(memberMembershipFromFirestore);
        } catch (error) {
            console.error("Erro ao buscar planos ativos do membro:", error);
            throw error;
        }
    },

    /**
     * Atualiza campos parciais de um plano do membro.
     */
    update: async (memberMembershipId, idTenant, authUser, updateFields = {}) => {
        if (!idTenant || !memberMembershipId) throw new Error("ID do Tenant e do Plano são obrigatórios.");

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, memberMembershipId);

            const updateData = {
                ...updateFields,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            await updateDoc(docRef, updateData);

            // Auditoria
            const changedFields = Object.keys(updateFields).join(", ");
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: memberMembershipId,
                entityName: "Atualização de plano",
                description: `Atualizou plano #${memberMembershipId.substring(0, 8)}: ${changedFields}`,
                details: updateFields
            });

            return { id: memberMembershipId, ...updateData };
        } catch (error) {
            console.error("Erro ao atualizar plano do membro:", error);
            throw error;
        }
    },

    /**
     * Cancela um plano do membro.
     */
    cancel: async (memberMembershipId, idTenant, authUser, membership, reason = "", effectiveCancelDate = "") => {
        if (!idTenant || !memberMembershipId || !membership) throw new Error("ID do Tenant, Plano e objeto membership são obrigatórios.");

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, memberMembershipId);

            const today = toISODate(new Date());
            const cancelDate = effectiveCancelDate || today;
            const isFuture = cancelDate > today;


            const updateData = {
                status: isFuture ? membership.status : "canceled",
                cancelDate: cancelDate,
                cancelReason: reason || "Cancelado pelo gestor",
                isScheduledCancel: isFuture,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            await updateDoc(docRef, updateData);

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: memberMembershipId,
                entityName: isFuture ? "Cancelamento de plano agendado" : "Cancelamento de plano",
                description: `${isFuture ? "Agendou cancelamento" : "Cancelou"} plano #${memberMembershipId.substring(0, 8)} para dia ${cancelDate}. Motivo: ${reason || "Não informado"}`
            });

            return { id: memberMembershipId, ...updateData };
        } catch (error) {
            console.error("Erro ao cancelar plano do membro:", error);
            throw error;
        }
    },

    /**
     * Transfere um período do plano atual para outro aluno.
     * Encerra (ou reduz) o tempo no plano de origem e cria um novo ativo no destino.
     */
    transfer: async (memberMembershipId, idTenant, authUser, sourceMembership, targetMemberId, daysToTransfer, reason = "") => {
        if (!idTenant || !memberMembershipId || !sourceMembership || !targetMemberId || !daysToTransfer) {
            throw new Error("Parâmetros incompletos para transferência.");
        }

        try {
            const today = toISODate(new Date());

            // 1. Calcular o fim do plano origem subtraindo os dias transferidos
            const originEndDateStr = sourceMembership.endDate;
            if (!originEndDateStr) throw new Error("Plano origem não possui data de término.");

            let originRemainingDays = 0;
            const diffTime = new Date(originEndDateStr + "T00:00:00").getTime() - new Date(today + "T00:00:00").getTime();
            if (diffTime > 0) originRemainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (daysToTransfer > originRemainingDays) {
                throw new Error("Quantidade de dias a transferir excede o saldo do contrato.");
            }

            // Subtrai da data fim atual
            const originEndObj = new Date(originEndDateStr + "T23:59:59");
            originEndObj.setDate(originEndObj.getDate() - daysToTransfer);
            const newOriginEndDate = toISODate(originEndObj);

            // Se transferiu tudo que sobrava (newOriginEndDate <= today), ele "cancela"
            const isFullTransfer = newOriginEndDate <= today;



            // Origem
            const docRefOrigin = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, memberMembershipId);
            const originUpdateData = {
                endDate: newOriginEndDate,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            if (isFullTransfer) {
                originUpdateData.status = "canceled";
                originUpdateData.cancelReason = `Transferido para ID ${targetMemberId} - ${reason}`;
                originUpdateData.cancelDate = today;
                originUpdateData.isTransfer = true; // Flag mágica para não somar churn!
            }

            await updateDoc(docRefOrigin, originUpdateData);

            // Destino
            const colRefTarget = collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS);

            // O novo contrato passa a valer de amanhã se for contínuo? Ou a partir de hoje? Geralmente hoje.
            const targetEndObj = new Date(today + "T23:59:59");
            targetEndObj.setDate(targetEndObj.getDate() + daysToTransfer);

            const newTargetMembership = {
                idMember: targetMemberId,
                membershipName: `Transferência: ${sourceMembership.membershipName}`,
                idMembershipTarget: sourceMembership.idMembershipTarget || sourceMembership.membershipId || null,
                startDate: today,
                endDate: toISODate(targetEndObj),
                status: "active",
                price: 0, // Receita = 0
                originalValue: 0,
                maxSuspensionDays: sourceMembership.maxSuspensionDays || 0,
                disponibleSuspensionDays: sourceMembership.disponibleSuspensionDays || 0,
                requiresClasses: sourceMembership.requiresClasses || false,
                classesPerWeek: sourceMembership.classesPerWeek || null,
                createdAt: serverTimestamp(),
                createdBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                },
                transferredFrom: memberMembershipId,
                transferReason: reason
            };

            await addDoc(colRefTarget, newTargetMembership);

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: memberMembershipId,
                entityName: "Transferência de plano",
                description: `Transferiu ${daysToTransfer} dia(s) do plano #${memberMembershipId.substring(0, 8)} para o membro ${targetMemberId}. Motivo: ${reason}`
            });

            return true;
        } catch (error) {
            console.error("Erro ao transferir plano:", error);
            throw error;
        }
    },

    /**
     * Suspende um plano do membro.
     */
    suspend: async (memberMembershipId, idTenant, authUser, membership, freezeData = {}) => {
        if (!idTenant || !memberMembershipId || !membership) throw new Error("ID do Tenant, Plano e objeto membership são obrigatórios.");

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, memberMembershipId);

            const { days = 0, reason = "", startDate = toISODate(new Date()) } = freezeData;

            // Prorrogar o fim do contrato automaticamente
            let newEndDate = membership.endDate;
            if (membership.endDate && days > 0) {
                const d = new Date(membership.endDate + "T23:59:59");
                d.setDate(d.getDate() + days);
                newEndDate = toISODate(d);
            }

            // Decrementar dias de suspensão disponíveis
            const currentDisponible = membership.disponibleSuspensionDays || 0;
            const newDisponible = Math.max(0, currentDisponible - days);

            // Registro completo da ação
            const suspensionRecord = {
                startDate,
                days,
                reason,
                createdAt: Timestamp.now(),
                createdBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            // Anexar no array local
            const currentFreezes = membership.freezes || [];
            const newFreezes = [...currentFreezes, suspensionRecord];

            const today = toISODate(new Date());
            const isFuture = startDate > today;

            const updateData = {
                status: isFuture ? membership.status : "suspended",
                endDate: newEndDate,
                disponibleSuspensionDays: newDisponible,
                freezes: newFreezes,
                hasScheduledSuspension: isFuture,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            await updateDoc(docRef, updateData);

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: memberMembershipId,
                entityName: isFuture ? "Suspensão de plano agendada" : "Suspensão de plano",
                description: `${isFuture ? "Agendou suspensão do" : "Suspendeu"} plano #${memberMembershipId.substring(0, 8)} por ${days} dias a partir de ${startDate}. Nova data fim: ${newEndDate}`
            });

            return { id: memberMembershipId, ...updateData };
        } catch (error) {
            console.error("Erro ao suspender plano do membro:", error);
            throw error;
        }
    },

    /**
     * Interrompe uma suspensão atual (ou cancela uma agendada), reativando o plano
     * e recalculando o estorno de dias e saldo.
     */
    endSuspension: async (memberMembershipId, idTenant, authUser, membership) => {
        if (!idTenant || !memberMembershipId || !membership) throw new Error("ID do Tenant, Plano e objeto membership são obrigatórios.");

        try {
            const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, memberMembershipId);

            const currentFreezes = membership.freezes || [];
            if (currentFreezes.length === 0) {
                throw new Error("Nenhum histórico de suspensão encontrado neste plano.");
            }

            // Pega o último registro cronológico
            const freezesCopy = [...currentFreezes];
            const lastFreeze = freezesCopy[freezesCopy.length - 1];

            const todayStr = toISODate(new Date());
            const startDateObj = new Date(lastFreeze.startDate + "T00:00:00");
            const todayObj = new Date(todayStr + "T00:00:00");

            // Dias transcorridos. Se menor que 0 (agendamento futuro), usado = 0.
            let usedDays = Math.floor((todayObj - startDateObj) / (1000 * 60 * 60 * 24));
            if (usedDays < 0) usedDays = 0;
            // Teto de uso é o total contratado no freeze
            if (usedDays > lastFreeze.days) usedDays = lastFreeze.days;

            const unusedDays = lastFreeze.days - usedDays;

            // Recalcula o término estornando (removendo) os dias não usados da extensão
            let newEndDate = membership.endDate;
            if (membership.endDate && unusedDays > 0) {
                const endObj = new Date(membership.endDate + "T23:59:59");
                endObj.setDate(endObj.getDate() - unusedDays);
                newEndDate = toISODate(endObj);
            }

            // Devolve o saldo não utilizado para consumo futuro
            const currentDisponible = membership.disponibleSuspensionDays || 0;
            const newDisponible = currentDisponible + unusedDays;

            // Atualiza o histórico retificando os dados reais
            freezesCopy[freezesCopy.length - 1] = {
                ...lastFreeze,
                days: usedDays,
                endedAt: Timestamp.now(),
                refundedDays: unusedDays
            };

            const updateData = {
                status: "active",
                endDate: newEndDate,
                disponibleSuspensionDays: newDisponible,
                freezes: freezesCopy,
                hasScheduledSuspension: false,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            };

            await updateDoc(docRef, updateData);

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: memberMembershipId,
                entityName: "Desbloqueio Antecipado (Suspensão)",
                description: `Destrancou plano #${memberMembershipId.substring(0, 8)}. Estornou ${unusedDays} dia(s). Novo fim do plano em: ${newEndDate}`
            });

            return { id: memberMembershipId, ...updateData };
        } catch (error) {
            console.error("Erro ao destrancar suspensão:", error);
            throw error;
        }
    }
};

export default MemberMembershipService;
